/**
 * Calendar dates stored as DATE ('YYYY-MM-DD').
 * new Date('YYYY-MM-DD') is UTC midnight, which is the previous local day
 * in timezones behind UTC. These helpers keep the calendar day.
 */

function dateOnlyKey(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || '').trim());
  if (!match) return '';
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function parseLocalDate(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const key = dateOnlyKey(value);
  if (!key) return null;
  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

function localDateString(date) {
  if (date instanceof Date) return dateOnlyKey(date);
  const key = dateOnlyKey(date);
  if (key) return key;
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  return dateOnlyKey(value);
}

function formatDateOnly(value, locales, options) {
  const date = parseLocalDate(value);
  if (!date) return '';
  return date.toLocaleDateString(locales, options);
}

function calendarDayDiff(value, now = new Date()) {
  const due = parseLocalDate(value);
  const today = parseLocalDate(now);
  if (!due || !today) return null;
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function isBeforeDateOnly(value, other) {
  const left = dateOnlyKey(value);
  const right = dateOnlyKey(other);
  return Boolean(left && right) && left < right;
}

function addCalendarDays(value, days) {
  const date = parseLocalDate(value);
  if (!date) return '';
  date.setDate(date.getDate() + days);
  return dateOnlyKey(date);
}

function tasksOnDay(tasks, year, monthIndex, day) {
  const key = dateOnlyKey(new Date(year, monthIndex, day));
  return (tasks || []).filter((task) => dateOnlyKey(task && task.due_date) === key);
}

const longDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const nyPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h12',
});

function pad2(value) {
  return String(value).padStart(2, '0');
}

function longDateFromKey(key) {
  const parts = String(key || '').split('-').map(Number);
  if (parts.length !== 3 || parts.some((part) => !part)) return '';
  const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12));
  return longDateFormatter.format(date);
}

/**
 * YYYY-MM-DD, and midnight UTC instants (Stripe period ends stored as
 * 00:00:00Z), are that calendar day. A time zone must not move them back.
 */
function utcCalendarKey(value) {
  const match = /^(\d{4}-\d{2}-\d{2})(?:[T ]00:00:00(?:\.0+)?(?:Z|\+00:?00)?)?$/.exec(String(value || '').trim());
  if (!match) return '';
  return parseLocalDate(match[1]) ? match[1] : '';
}

function isUtcMidnight(date) {
  return date.getUTCHours() === 0
    && date.getUTCMinutes() === 0
    && date.getUTCSeconds() === 0
    && date.getUTCMilliseconds() === 0;
}

function formatInNewYork(date) {
  const parts = nyPartsFormatter.formatToParts(date);
  const pick = (type) => {
    const part = parts.find((item) => item.type === type);
    return part ? part.value : '';
  };
  const dateLabel = `${pick('month')} ${pick('day')}, ${pick('year')}`;
  const hour = String(pick('hour') || '').replace(/^0/, '');
  const minute = pick('minute');
  const dayPeriod = String(pick('dayPeriod') || '').toUpperCase();
  if (hour === '12' && minute === '00' && dayPeriod === 'AM') return dateLabel;
  if (!hour || !minute || !dayPeriod) return dateLabel;
  return `${dateLabel}, ${hour}:${minute} ${dayPeriod} ET`;
}

/**
 * Email dates are long, like November 15, 2026.
 * A YYYY-MM-DD value, or a midnight-UTC instant, stays on that calendar day.
 * Any other Date, ISO string, or number is shown in America/New_York, with
 * the clock when it is not midnight there. Numbers below 1e12 are Stripe
 * unix seconds. Empty or unusable input is ''.
 */
function formatEmailDate(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const key = utcCalendarKey(value);
    if (key) return longDateFromKey(key);
  }
  const date = emailInstant(value);
  if (!date) return '';
  if (isUtcMidnight(date)) {
    return longDateFromKey(`${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`);
  }
  return formatInNewYork(date);
}

function emailInstant(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const ms = Math.abs(value) < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value !== 'string') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

module.exports = {
  dateOnlyKey,
  parseLocalDate,
  localDateString,
  formatDateOnly,
  formatEmailDate,
  calendarDayDiff,
  isBeforeDateOnly,
  addCalendarDays,
  tasksOnDay,
};
